import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type RSVP from 'rsvp';
import { allSettled } from 'rsvp';

import type { PopupData, RequestError, Service, ValidationErrors } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sortBy } from 'webapp/utils/common';

import validationMessage from 'webapp/helpers/validation-message';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import FormSelectComponent from 'webapp/components/form/select';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

import Board from 'webapp/models-custom/board';
import type SpaceRecord from 'webapp/models/space';

import { Severity } from 'webapp/services/logger';

// a space record, or only the id of a space typed in by id
type TargetSpace = Pick<SpaceRecord, 'id'> &
	Partial<Pick<SpaceRecord, 'companyName' | 'boardCount'>> & { featureBoards?: number };

export default class BoardPopupTransferComponent extends PopupInnerModalComponent<PopupData.Board.Transfer> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('logger') declare loggerService: Service.Logger;
	@service('request') declare requestService: Service.Request;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;

	@tracked selectedSpacesIds: string[] = [];
	@tracked toSpace = '';
	@tracked transferLoop = false; // logic is currently unused (commented)
	@tracked errors: ValidationErrors<'boardsLimit'> = {};

	_showSpaceId = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.popupData.showSpaceId) {
			this._showSpaceId = true;
		}
	}

	get model() {
		return this.popupData.model;
	}

	get toAgencyOnly() {
		return this.popupData.toAgency;
	}

	get nameInTitle() {
		return this.model instanceof Board
			? this.model.name
			: this.model.map((b) => b.name).join(', ');
	}

	get allAvailableSpaces() {
		const allAvailableSpaces: LabelValuePairString[] = [];

		const currentSpaceId = String(this.accountService.id);

		this.mmSpacesService.otherSpaceRecords.forEach((space) => {
			if (space.id !== currentSpaceId) {
				allAvailableSpaces.push({
					value: space.id!, // loaded records
					label: space.companyName,
				});
			}
		});

		return sortBy(allAvailableSpaces, 'label');
	}

	get selectedSpaces() {
		let selectedSpaces: TargetSpace[] = [];

		if (this.showSpaceId && this.toSpace) {
			selectedSpaces = [{ id: this.toSpace }];
		} else {
			if (this.toAgencyOnly) {
				const agencySpace = this.mmSpacesService.agencySpaceRecord;
				if (agencySpace) {
					selectedSpaces = [agencySpace];
				}
			} else if (this.selectedSpacesIds?.length) {
				this.selectedSpacesIds.forEach((selectedSpaceId) => {
					const space = this.mmSpacesService.findRecord(String(selectedSpaceId));
					if (space) {
						selectedSpaces.push(space);
					}
				});
			}
		}

		return selectedSpaces;
	}

	get showSpaceId(): boolean {
		return this.currentUserService.allowTransferToSpaceId || this._showSpaceId;
	}

	set showSpaceId(spaceId: boolean) {
		this._showSpaceId = spaceId;
	}

	get boardsToTransfer(): number {
		// return this.transferLoop ? this.model.allCarouseledBoards.length : 1;
		return 1;
	}

	// get transferLoopLabel(): string{
	// 	const boardsInLoopCount = this.model.loopIds.length + 1;

	// 	return `Copy all looped Databoards (${boardsInLoopCount}) with this Databoard`;
	// }

	validateBoardLimitations(): boolean {
		const spacesInError: string[] = [],
			errors: ValidationErrors<'boardsLimit'> = {},
			boardsToTransfer = this.boardsToTransfer;
		let spacesLeft;

		this.selectedSpaces.forEach((space) => {
			if (space.id === this.toSpace) {
				spacesLeft = Infinity;
			} else {
				// never enforced since 2020: featureBoards is always undefined (the space model lost the
				// attribute in Sept 2020 and BE stopped sending space limitations in July 2021).
				// Fix or remove: https://app.asana.com/1/7582066485656/project/8014827585025/task/1219777079431275
				const allowedBoardCount = space.featureBoards || Infinity;

				spacesLeft = allowedBoardCount - (space.boardCount ?? 0);
			}

			const left = spacesLeft - boardsToTransfer;
			if (left < 0) {
				spacesInError.push(space.companyName ?? String(space.id));
			}
		});

		if (spacesInError.length) {
			let message;

			if (this.toAgencyOnly) {
				message =
					'The organization has reached its dashboard limit. Upgrade the plan or delete existing dashboards to continue.';
			} else {
				const accountText =
					spacesInError.length > 1
						? `accounts (${spacesInError.join(', ')})`
						: `account (${spacesInError[0]})`;

				message = `The organization has reached its dashboard limit, so no additional dashboards can be added to the selected ${accountText}. Upgrade the organization plan or delete existing dashboards to continue.`;
			}

			errors.boardsLimit = [{ message }];
		}

		this.errors = errors;

		return Object.keys(errors).length === 0;
	}

	// @action
	// changeTransferLoop(transferLoop: boolean): void {
	// 	this.transferLoop = transferLoop;
	// }

	@action
	changeSelectedSpacesIds(selectedSpacesIds: string[]): void {
		this.selectedSpacesIds = selectedSpacesIds;
		this.scheduleReposition();
	}

	@action
	transferBoard(): void {
		if (!this.validateBoardLimitations() || !this.selectedSpaces.length) {
			return;
		}

		this.submitInProgress = true;
		const model = this.model;
		if (model instanceof Board) {
			const promises: Promise<unknown>[] = [];
			this.selectedSpaces.forEach((space) => {
				const url = `/boards/${model.id}/transfer`,
					params = {
						spaceId: space.id,
						transferLoop: this.transferLoop,
					};

				if (!this.currentUserService.isAgencyUserInClientSpace && !this.showSpaceId) {
					this.analyticsService.event(TrackingEvent.SEND_BOARD_TO_CLIENT, {
						looped: params.transferLoop,
					});
				}

				promises.push(
					this.requestService.api('POST', url, params, {
						skipShowingErrorInfolayer: true,
					}),
				);
			});

			allSettled(promises).then((statusArray) => {
				// request service requests, which reject with a RequestError
				const rejectedRequests = statusArray.filter(
					(item): item is RSVP.Rejected<RequestError> =>
						item.state === ('rejected' as RSVP.State),
				);
				if (rejectedRequests.length) {
					this.loggerService.logError(
						`[Transfer Board] POST failed to transfer board`,
						{
							failedRequestsCount: rejectedRequests.length,
							errors: JSON.stringify(rejectedRequests),
						},
						Severity.ERROR,
					);

					rejectedRequests.forEach((item) => {
						const error =
							item.reason.errors?.[0]?.message ||
							'There was a problem transferring your dashboard';
						this.infolayerService.error(error);
					});
				} else {
					this.infolayerService.ok('Dashboards have been successfully transferred!');
				}
			});
		} else {
			const numericSelectedSpacesIds = this.selectedSpacesIds
				.map(Number)
				.filter((id) => !isNaN(id));
			void this.mmBoardsService.batchCopyToSpace(model, numericSelectedSpacesIds);
		}

		this.submitInProgress = false;
		this.args.model.triggerEvent('confirm');
		this.close();
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Share" @size="lg" />
			Copy "{{this.nameInTitle}}" to
			{{#if this.toAgencyOnly}}the organization{{else}}another account{{/if}}
		</div>

		<div class="content">

			{{#if this.toAgencyOnly}}

				<p>A copy of this dashboard will be sent to your organization.</p>

			{{else}}

				<p>
					Copy this dashboard to other accounts within your organization, excluding the
					data. Select the destination:
				</p>

				<div class="target {{if this.showSpaceId 'double'}}">
					<div class="input1">
						<FormSelectComponent
							name="otherSources"
							@options={{this.allAvailableSpaces}}
							@selected={{this.selectedSpacesIds}}
							@placeholder="Select account"
							@tooltipOnSelectionFromProperty=""
							@showFloatlabel={{false}}
							@isRequired={{true}}
							@isDisabled={{this.submitInProgress}}
							@isNumeric={{false}}
							@isMultiple={{true}}
							@onChange={{this.changeSelectedSpacesIds}}
						/>
						<RowErrorComponent @items={{this.errors.boardsLimit}} />
					</div>
					<div class="input2">
						{{#if this.showSpaceId}}
							<FormInputComponent
								name="toSpace"
								@type="number"
								placeholder="spaceId"
								disabled={{this.submitInProgress}}
								@value={{this.toSpace}}
							/>
						{{/if}}
					</div>
				</div>

			{{/if}}

			<div class="clearfix"></div>

			{{#if this.errors.boardsLimit}}
				<div class="errorMessage skipFormError force_block">
					<CommonUiIconComponent @name="AlertFill" @size="sm" />
					<span class="message">{{validationMessage this.errors.boardsLimit}}</span>
				</div>
			{{/if}}

			<small class="note">
				Note: The dashboard will NOT be copied before it is accepted in the destination
				account. Until then all further changes to the dashboard will be included.
			</small>
			{{!--
    {{#if this.model.isCarouselRoot}}

        <div class="bluebox">

            <strong class="bluebox__title"><Common::UiIcon @name="Loop" @size="md" /> Looped dashboards is turned on for this dashboard</strong>

            <Form::Checkbox
                @name="transferLoop"
                @selected={{this.transferLoop}}
                @label={{this.transferLoopLabel}}
                @isDisabled={{this.disableTransferLoop}}
                @onChange={{this.changeTransferLoop}}
            />

            <ul class="bluebox__list">
                {{#each this.model.allCarouseledBoards as |board|}}
                    <li>{{board.name}}</li>
                {{/each}}
            </ul>

        </div>

    {{/if}}
    --}}
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Send copy"
						@extraClass="save"
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.transferBoard}}
					/>
				</div>
			</div>

		</div>
	</template>
}
